// Builds the full lead-notification email as HTML, styled to match the /alf
// landing page (A Place for Mom palette: cream ground, indigo accents, Optima
// headings). The EmailJS template is a single {{{message_html}}} pass-through,
// so every design change lives here in code instead of the EmailJS dashboard.

const INK = "#2C2F34";
const INDIGO = "#4F6AC5";
const INDIGO_DEEP = "#334FA9";
const CREAM = "#FFFDF7";
const BEIGE = "#F7F3E9";
const BLUE_CARD = "#DBEDFF";
const LINE = "#E7E2D6";
const MUTED = "#5A5E66";
const SERIF = "Optima, 'Marcellus', Candara, Georgia, serif";
const SANS = "'Poppins', 'Helvetica Neue', Arial, sans-serif";

const esc = (v) =>
  String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const row = (label, value, opts = {}) => {
  if (!value) return "";
  const strong = opts.strong ? "font-weight:600;" : "";
  const color = opts.color || INK;
  return `<tr>
    <td style="padding:11px 0;border-bottom:1px solid ${LINE};width:180px;color:${MUTED};font-size:13px;font-family:${SANS};">${esc(label)}</td>
    <td style="padding:11px 0;border-bottom:1px solid ${LINE};color:${color};font-size:15px;font-family:${SANS};${strong}">${opts.raw ? value : esc(value)}</td>
  </tr>`;
};

const section = (title, rows) => {
  if (!rows.trim()) return "";
  return `<tr><td style="background-color:#ffffff;padding:26px 34px 6px 34px;">
    <div style="font-family:${SANS};font-size:11px;letter-spacing:2px;color:${INDIGO_DEEP};text-transform:uppercase;font-weight:600;margin-bottom:10px;">${esc(title)}</div>
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%">${rows}</table>
  </td></tr>`;
};

export function buildLeadEmailHtml(d) {
  const isMedicaid = d.medicaid === true || d.paymentSource === "Medicaid";
  const recipient = [d.recipientName, d.recipientAge ? `${d.recipientAge}` : ""].filter(Boolean).join(", ");
  const where = [d.city, d.state, d.zip].filter(Boolean).join(", ");

  const summary = `${esc(d.fullName)}${d.contactRelationship ? ` (${esc(d.contactRelationship)})` : ""} is looking for
    <b>${esc(d.careType || "senior living")}</b>${recipient ? ` for ${esc(recipient)}` : ""} in <b>${esc(d.preferredLocation || "South Florida")}</b>.
    Budget <b>${esc(d.budget || "not specified")}</b>${d.paymentSource ? `, paid via ${esc(d.paymentSource)}` : ""}.
    Move-in: <b>${esc(d.timeline || "not specified")}</b>.`;

  const medicaidBanner = isMedicaid
    ? `<tr><td style="background-color:#FDECEA;padding:14px 34px;border-top:1px solid #F5C6C0;border-bottom:1px solid #F5C6C0;">
         <div style="font-family:${SANS};font-size:13.5px;font-weight:600;color:#9B2C1F;">Medicaid lead - not forwarded to the community.</div>
       </td></tr>`
    : "";

  return `<div style="margin:0;padding:26px 12px;background-color:${BEIGE};font-family:${SANS};">
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="max-width:640px;margin:0 auto;border:1px solid ${LINE};border-radius:18px;overflow:hidden;background-color:${CREAM};">
    <tr>
      <td style="background-color:#ffffff;padding:22px 34px;border-bottom:1px solid ${LINE};text-align:center;">
        <div style="font-family:${SERIF};font-size:24px;color:${INK};">Emetya <span style="color:${INDIGO_DEEP};">| Assisted Living</span></div>
      </td>
    </tr>
    <tr>
      <td style="background-color:${BLUE_CARD};padding:26px 34px;text-align:center;">
        <div style="font-family:${SANS};font-size:11px;letter-spacing:2.5px;color:${INDIGO_DEEP};text-transform:uppercase;font-weight:600;margin-bottom:8px;">New Lead</div>
        <div style="font-family:${SERIF};font-size:30px;color:${INK};line-height:1.2;">${esc(d.fullName)}</div>
        <div style="font-family:${SANS};font-size:14px;color:${MUTED};margin-top:6px;">wants pricing &amp; availability in ${esc(d.preferredLocation || "South Florida")}</div>
      </td>
    </tr>
    ${medicaidBanner}
    <tr>
      <td style="background-color:${CREAM};padding:22px 34px;border-bottom:1px solid ${LINE};">
        <div style="font-family:${SANS};font-size:15px;color:${INK};line-height:1.6;">${summary}</div>
      </td>
    </tr>
    ${section("Contact", `
      ${row("Name", d.fullName, { strong: true })}
      ${row("Phone", `<a href="tel:${esc(d.phone)}" style="color:${INDIGO_DEEP};font-weight:600;text-decoration:none;">${esc(d.phone)}</a>`, { raw: true })}
      ${row("Email", `<a href="mailto:${esc(d.email)}" style="color:${INDIGO_DEEP};text-decoration:none;">${esc(d.email)}</a>`, { raw: true })}
      ${row("Relationship", d.contactRelationship)}
      ${row("Submitted from", where)}
    `)}
    ${section("Care Recipient", `
      ${row("Who needs care", d.whoNeedsCare, { strong: true })}
      ${row("Name", d.recipientName)}
      ${row("Age", d.recipientAge)}
      ${row("Care type", d.careType, { strong: true })}
      ${row("Memory loss", d.memoryLoss)}
      ${row("Help needed with", d.careNeeds)}
      ${row("Current living situation", d.currentLiving)}
    `)}
    ${section("Budget & Timing", `
      ${row("Monthly budget", d.budget, { strong: true })}
      ${row("Payment source", d.paymentSource, { strong: true })}
      ${row("Medicaid", isMedicaid ? "Yes" : "No", { color: isMedicaid ? "#9B2C1F" : INK })}
      ${row("Move-in timeline", d.timeline, { strong: true })}
      ${row("Preferred location", d.preferredLocation, { strong: true })}
      ${row("Routes to advisor", d.advisorEmail)}
    `)}
    <tr>
      <td style="background-color:${CREAM};padding:26px 34px 30px 34px;text-align:center;border-top:1px solid ${LINE};">
        <a href="https://app.genaya.com/leads/table/BMaOMvpAbvbfCVWCRc3i" style="display:inline-block;background-color:${INDIGO};color:#ffffff;font-family:${SANS};font-size:14px;font-weight:600;padding:14px 36px;border-radius:50px;text-decoration:none;">Open in Genaya CRM</a>
        <div style="font-family:${SANS};font-size:11px;color:#9AA1A8;margin-top:18px;line-height:1.6;">
          Submitted ${esc(d.submittedAt || new Date().toISOString())}<br/>
          Source: ${esc(d.source || "emetya.com/alf")}
        </div>
      </td>
    </tr>
  </table>
</div>`;
}

// The same record, addressed to the AHAVA community advisor. Adds the referral
// terms footer that placement agencies (A Place for Mom, Senior Advantages) use
// to protect the referral against a late "we already had this lead" claim.
export function buildAdvisorEmailHtml(d) {
  const base = buildLeadEmailHtml({ ...d, source: "Emetya referral" });
  const terms = `<div style="max-width:640px;margin:14px auto 0 auto;font-family:${SANS};font-size:11px;color:#8A9199;line-height:1.65;">
    This referral is provided by Emetya / Genesis under our referral agreement. If this prospect already exists in your records,
    please notify us by reply within two (2) business days of receipt; otherwise the referral is considered original to Emetya.
    Referral fee applies per agreement after 30 consecutive days of paid residency (private pay only).
  </div>`;
  return base.replace("</div>", terms + "</div>");
}

// Confirmation sent to the family who submitted. Reassurance, not a sales blast:
// they have just handed over a parent's care details and want to know a human
// will call. Mirrors the landing page so the brand feels continuous.
export function buildConfirmationEmailHtml(d) {
  const recap = [
    d.careType && ["Care type", d.careType],
    d.preferredLocation && ["Preferred location", d.preferredLocation],
    d.timeline && ["Move-in timeline", d.timeline],
  ].filter(Boolean).map(([k, v]) => `
    <tr>
      <td style="padding:9px 0;border-bottom:1px solid ${LINE};width:170px;color:${MUTED};font-size:13px;font-family:${SANS};">${esc(k)}</td>
      <td style="padding:9px 0;border-bottom:1px solid ${LINE};color:${INK};font-size:15px;font-family:${SANS};font-weight:600;">${esc(v)}</td>
    </tr>`).join("");

  const step = (n, title, text) => `
    <tr>
      <td width="44" valign="top" style="padding:10px 0;">
        <div style="width:32px;height:32px;border-radius:50%;background-color:${BLUE_CARD};color:${INDIGO_DEEP};font-family:${SANS};font-size:14px;font-weight:700;text-align:center;line-height:32px;">${n}</div>
      </td>
      <td valign="top" style="padding:10px 0;">
        <div style="font-family:${SANS};font-size:15px;font-weight:600;color:${INK};">${esc(title)}</div>
        <div style="font-family:${SANS};font-size:13.5px;color:${MUTED};line-height:1.55;margin-top:3px;">${esc(text)}</div>
      </td>
    </tr>`;

  return `<div style="margin:0;padding:26px 12px;background-color:${BEIGE};font-family:${SANS};">
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="max-width:620px;margin:0 auto;border:1px solid ${LINE};border-radius:18px;overflow:hidden;background-color:${CREAM};">
    <tr>
      <td style="background-color:#ffffff;padding:22px 34px;border-bottom:1px solid ${LINE};text-align:center;">
        <div style="font-family:${SERIF};font-size:24px;color:${INK};">Emetya <span style="color:${INDIGO_DEEP};">| Assisted Living</span></div>
      </td>
    </tr>
    <tr>
      <td style="padding:34px 34px 8px 34px;text-align:center;">
        <div style="font-family:${SERIF};font-size:30px;color:${INK};line-height:1.25;">Thank you, ${esc((d.fullName || "").split(" ")[0] || "there")}</div>
        <div style="font-family:${SANS};font-size:15.5px;color:${MUTED};line-height:1.6;margin-top:12px;">
          We have received your request for pricing and availability at <b style="color:${INK};">AHAVA Senior Residences</b>.
          A senior living advisor will contact you within one business day.
        </div>
      </td>
    </tr>
    <tr>
      <td style="padding:22px 34px 6px 34px;">
        <div style="font-family:${SANS};font-size:11px;letter-spacing:2px;color:${INDIGO_DEEP};text-transform:uppercase;font-weight:600;margin-bottom:6px;">What happens next</div>
        <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
          ${step(1, "An advisor reviews your request", "Someone from the AHAVA team looks at what your family needs.")}
          ${step(2, "You get a call or email", "Within one business day, with pricing and current availability.")}
          ${step(3, "Visit if you would like", "A private tour of the community, with no obligation at any point.")}
        </table>
      </td>
    </tr>
    ${recap ? `<tr><td style="padding:16px 34px 6px 34px;">
      <div style="font-family:${SANS};font-size:11px;letter-spacing:2px;color:${INDIGO_DEEP};text-transform:uppercase;font-weight:600;margin-bottom:8px;">What you told us</div>
      <table role="presentation" cellpadding="0" cellspacing="0" width="100%">${recap}</table>
    </td></tr>` : ""}
    <tr>
      <td style="padding:24px 34px 30px 34px;">
        <div style="background-color:#ffffff;border:1px solid ${LINE};border-radius:14px;padding:20px 22px;">
          <div style="font-family:${SERIF};font-size:19px;color:${INK};margin-bottom:8px;">About AHAVA Senior Residences</div>
          <div style="font-family:${SANS};font-size:13.5px;color:${MUTED};line-height:1.6;">
            Family-owned and operated since 2008, with communities in Aventura and Boca Raton.
            Assisted living and memory care under one roof, chef-prepared dining, daily activities,
            and a 24/7 care team. Recognized by U.S. News &amp; World Report as Best Memory Care 2025.
          </div>
        </div>
      </td>
    </tr>
    <tr>
      <td style="background-color:${CREAM};padding:20px 34px 26px 34px;border-top:1px solid ${LINE};text-align:center;">
        <div style="font-family:${SANS};font-size:11.5px;color:#9AA1A8;line-height:1.65;">
          You are receiving this because you requested pricing at emetya.com. Emetya is an independent
          comparison service, not a healthcare provider, and may receive compensation from featured communities.
          Information provided is for general purposes and is not medical advice.
        </div>
      </td>
    </tr>
  </table>
</div>`;
}
